import { useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, Flame, Snowflake, Activity, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGSCKPIs, useGSCPerformanceOverTime } from '@/hooks/useGSCData';
import { useGA4KPIs, useGA4TrafficOverTime } from '@/hooks/useGA4Data';
import { useAuthStore } from '@/stores/auth-store';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';

/**
 * Momentum View - Shows the "velocity" of your metrics
 * Inspired by stock tickers and fitness trackers
 * Focuses on trends and momentum rather than absolute numbers
 */
export function MomentumView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGSC = !!activeProperty?.gscSite;
  const hasGA4 = !!activeProperty?.ga4Property;

  const { data: gscKpis, isLoading: gscKpisLoading } = useGSCKPIs();
  const { data: ga4Kpis, isLoading: ga4KpisLoading } = useGA4KPIs();
  const { data: gscOverTime, isLoading: gscOverTimeLoading } = useGSCPerformanceOverTime();
  const { data: ga4OverTime, isLoading: ga4OverTimeLoading } = useGA4TrafficOverTime();

  const isLoading = gscKpisLoading || ga4KpisLoading || gscOverTimeLoading || ga4OverTimeLoading;

  // Calculate overall momentum score
  const momentumScore = useMemo(() => {
    if (!gscKpis && !ga4Kpis) return null;

    const changes = [
      gscKpis?.clicksChange,
      gscKpis?.impressionsChange,
      ga4Kpis?.sessionsChange,
      ga4Kpis?.usersChange,
    ].filter((c): c is number => c !== undefined);

    if (changes.length === 0) return null;

    const avgChange = changes.reduce((a, b) => a + b, 0) / changes.length;
    return avgChange;
  }, [gscKpis, ga4Kpis]);

  const getMomentumStatus = (score: number | null) => {
    if (score === null) return { label: 'Unknown', icon: Activity, color: 'muted' };
    if (score > 20) return { label: 'On Fire', icon: Flame, color: 'success' };
    if (score > 5) return { label: 'Growing', icon: TrendingUp, color: 'success' };
    if (score > -5) return { label: 'Stable', icon: Minus, color: 'warning' };
    if (score > -20) return { label: 'Declining', icon: TrendingDown, color: 'danger' };
    return { label: 'Cooling', icon: Snowflake, color: 'danger' };
  };

  const status = getMomentumStatus(momentumScore);

  if (!hasGSC && !hasGA4) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No property connected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
          <Activity className="h-6 w-6 text-primary" />
          Momentum Tracker
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Track the velocity and direction of your key metrics
        </p>
      </div>

      {/* Overall Momentum Score */}
      <div className="glass-panel p-8 text-center">
        {isLoading ? (
          <div className="animate-pulse">
            <div className="h-20 w-20 rounded-full bg-muted mx-auto" />
            <div className="h-6 w-32 bg-muted rounded mt-4 mx-auto" />
          </div>
        ) : (
          <>
            <div className={cn(
              'h-24 w-24 rounded-full mx-auto flex items-center justify-center',
              status.color === 'success' && 'bg-success/20 text-success ring-4 ring-success/30',
              status.color === 'warning' && 'bg-warning/20 text-warning ring-4 ring-warning/30',
              status.color === 'danger' && 'bg-danger/20 text-danger ring-4 ring-danger/30',
              status.color === 'muted' && 'bg-muted text-muted-foreground ring-4 ring-muted'
            )}>
              <status.icon className="h-12 w-12" />
            </div>
            <p className={cn(
              'text-3xl font-bold mt-4',
              status.color === 'success' && 'text-success',
              status.color === 'warning' && 'text-warning',
              status.color === 'danger' && 'text-danger',
              status.color === 'muted' && 'text-muted-foreground'
            )}>
              {status.label}
            </p>
            {momentumScore !== null && (
              <p className="text-sm text-muted-foreground mt-2">
                {momentumScore >= 0 ? '+' : ''}{momentumScore.toFixed(1)}% average change
              </p>
            )}
          </>
        )}
      </div>

      {/* Metric Tickers */}
      <div className="grid grid-cols-2 gap-4">
        {hasGSC && (
          <>
            <MomentumTicker
              label="Clicks"
              value={gscKpis?.clicks ?? 0}
              change={gscKpis?.clicksChange ?? 0}
              data={gscOverTime?.map((d: { clicks: number }) => ({ value: d.clicks })) ?? []}
              isLoading={isLoading}
              color="emerald"
            />
            <MomentumTicker
              label="Impressions"
              value={gscKpis?.impressions ?? 0}
              change={gscKpis?.impressionsChange ?? 0}
              data={gscOverTime?.map((d: { impressions: number }) => ({ value: d.impressions })) ?? []}
              isLoading={isLoading}
              color="emerald"
            />
          </>
        )}
        {hasGA4 && (
          <>
            <MomentumTicker
              label="Sessions"
              value={ga4Kpis?.sessions ?? 0}
              change={ga4Kpis?.sessionsChange ?? 0}
              data={ga4OverTime?.map((d: { sessions: number }) => ({ value: d.sessions })) ?? []}
              isLoading={isLoading}
              color="blue"
            />
            <MomentumTicker
              label="Users"
              value={ga4Kpis?.users ?? 0}
              change={ga4Kpis?.usersChange ?? 0}
              data={ga4OverTime?.map((d: { users: number }) => ({ value: d.users })) ?? []}
              isLoading={isLoading}
              color="blue"
            />
          </>
        )}
      </div>

      {/* Rate Metrics */}
      <div className="grid grid-cols-3 gap-4">
        {hasGSC && (
          <>
            <RateTicker
              label="CTR"
              value={gscKpis?.ctr ?? 0}
              change={gscKpis?.ctrChange ?? 0}
              suffix="%"
              isLoading={isLoading}
            />
            <RateTicker
              label="Avg Position"
              value={gscKpis?.position ?? 0}
              change={gscKpis?.positionChange ?? 0}
              invertChange
              isLoading={isLoading}
            />
          </>
        )}
        {hasGA4 && (
          <RateTicker
            label="Bounce Rate"
            value={ga4Kpis?.bounceRate ?? 0}
            change={ga4Kpis?.bounceRateChange ?? 0}
            suffix="%"
            invertChange
            isLoading={isLoading}
          />
        )}
      </div>

      {/* Trend Visualization */}
      <div className="glass-panel p-6">
        <h2 className="text-lg font-medium mb-4">28-Day Trend</h2>
        <div className="grid grid-cols-2 gap-6">
          {hasGSC && gscOverTime && (
            <TrendChart
              label="Search Performance"
              data={gscOverTime.map((d: { date: string; clicks: number; impressions: number }) => ({
                date: d.date,
                clicks: d.clicks,
                impressions: d.impressions / 10, // Scale down for visibility
              }))}
              dataKey="clicks"
              color="#10b981"
            />
          )}
          {hasGA4 && ga4OverTime && (
            <TrendChart
              label="Site Traffic"
              data={ga4OverTime.map((d: { date: string; sessions: number; users: number }) => ({
                date: d.date,
                sessions: d.sessions,
                users: d.users,
              }))}
              dataKey="sessions"
              color="#3b82f6"
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface MomentumTickerProps {
  label: string;
  value: number;
  change: number;
  data: { value: number }[];
  isLoading: boolean;
  color: 'emerald' | 'blue';
}

function MomentumTicker({ label, value, change, data, isLoading, color }: MomentumTickerProps) {
  const isPositive = change >= 0;
  const chartColor = color === 'emerald' ? '#10b981' : '#3b82f6';
  const bgColor = color === 'emerald' ? 'bg-emerald-500/5' : 'bg-blue-500/5';
  const borderColor = color === 'emerald' ? 'border-l-emerald-500' : 'border-l-blue-500';

  return (
    <div className={cn('glass-panel p-4 border-l-4', borderColor, bgColor)}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className={cn('text-3xl font-bold text-foreground mt-1', isLoading && 'animate-pulse')}>
            {isLoading ? '-' : value.toLocaleString()}
          </p>
        </div>
        <div className={cn(
          'flex items-center gap-1 px-2 py-1 rounded text-sm font-medium',
          isPositive ? 'bg-success/20 text-success' : 'bg-danger/20 text-danger'
        )}>
          {isPositive ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
          {Math.abs(change).toFixed(1)}%
        </div>
      </div>
      {/* Mini sparkline */}
      <div className="h-12 mt-3 -mx-2">
        {data.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <Area
                type="monotone"
                dataKey="value"
                stroke={chartColor}
                fill={chartColor}
                fillOpacity={0.2}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

interface RateTickerProps {
  label: string;
  value: number;
  change: number;
  suffix?: string;
  invertChange?: boolean;
  isLoading: boolean;
}

function RateTicker({ label, value, change, suffix = '', invertChange = false, isLoading }: RateTickerProps) {
  // For metrics like bounce rate and position, lower is better
  const isPositive = invertChange ? change <= 0 : change >= 0;

  return (
    <div className="glass-panel p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="flex items-end gap-2 mt-1">
        <p className={cn('text-2xl font-bold text-foreground', isLoading && 'animate-pulse')}>
          {isLoading ? '-' : `${value.toFixed(1)}${suffix}`}
        </p>
        <div className={cn(
          'flex items-center gap-0.5 text-xs font-medium mb-1',
          isPositive ? 'text-success' : 'text-danger'
        )}>
          {isPositive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
          {Math.abs(change).toFixed(1)}%
        </div>
      </div>
    </div>
  );
}

interface TrendChartProps {
  label: string;
  data: any[];
  dataKey: string;
  color: string;
}

function TrendChart({ label, data, dataKey, color }: TrendChartProps) {
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-2">{label}</p>
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                border: '1px solid #374151',
                borderRadius: '8px',
              }}
              labelStyle={{ color: '#9ca3af' }}
              itemStyle={{ color: color }}
            />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              fill={color}
              fillOpacity={0.2}
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
